<template>
  <div class="idea-proposals">
    <div class="header">
      <div>
        <h2>Idea Proposals</h2>
        <p>Ideas for new collections and refinements of existing ones, from first proposal to Phase 1.</p>
      </div>
      <div class="header-actions">
        <el-button @click="openImport">Import JSON</el-button>
        <el-button type="primary" @click="openEditor()">New Proposal</el-button>
      </div>
    </div>

    <div class="filters">
      <el-radio-group v-model="filters.status" size="small" @change="reload">
        <el-radio-button label="">All ({{ allCount }})</el-radio-button>
        <el-radio-button v-for="s in statuses" :key="s.value" :label="s.value">
          {{ s.label }} ({{ counts[s.value] || 0 }})
        </el-radio-button>
      </el-radio-group>
      <div class="filter-row">
        <el-select v-model="filters.proposal_type" size="small" placeholder="All types" clearable style="width: 190px" @change="reload">
          <el-option v-for="t in types" :key="t.value" :label="t.label" :value="t.value" />
        </el-select>
        <el-input
          v-model="filters.q"
          size="small"
          clearable
          placeholder="Search title, idea, series, curator, tag"
          prefix-icon="el-icon-search"
          style="width: 320px"
          aria-label="Search proposals"
          @input="debouncedReload"
          @clear="reload"
        />
        <el-button size="small" :loading="loading" @click="load">Refresh</el-button>
      </div>
    </div>

    <el-alert v-if="loadError" :title="loadError" type="error" :closable="false" class="alert" />

    <el-table v-loading="loading" :data="items" empty-text="No proposals match these filters." style="width: 100%">
      <el-table-column label="P" width="56" align="center">
        <template slot-scope="{ row }">
          <span v-if="row.priority" class="priority" :class="'p' + row.priority">P{{ row.priority }}</span>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="Proposal" min-width="280">
        <template slot-scope="{ row }">
          <a class="title-link" href="#" @click.prevent="openDetail(row)">{{ row.title }}</a>
          <div class="idea">{{ row.main_idea }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Type" min-width="150">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.proposal_type === 'refine_collection' ? 'warning' : ''">{{ typeLabel(row.proposal_type) }}</el-tag>
          <div v-if="row.target_collection" class="sub">→ {{ row.target_collection.title }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Curators / series" min-width="160">
        <template slot-scope="{ row }">
          <div>{{ curatorNames(row) || '—' }}</div>
          <div v-if="row.series" class="sub">{{ row.series }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Shape" width="120">
        <template slot-scope="{ row }">
          <div>{{ directionLabel(row.direction) || '—' }}</div>
          <div class="sub">{{ timeSpanLabel(row.time_span) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Status" width="160">
        <template slot-scope="{ row }">
          <el-tag size="small" :type="statusTag(row.status)">{{ statusLabel(row.status) }}</el-tag>
          <div class="sub">Updated {{ formatDate(row.update_time) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="Actions" width="230">
        <template slot-scope="{ row }">
          <div class="row-actions">
            <el-button size="mini" @click="openDetail(row)">View</el-button>
            <el-button size="mini" @click="openEditor(row)">Edit</el-button>
            <el-button size="mini" type="primary" plain @click="openStatus(row)">Status</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="total > pageSize"
      class="pager"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page.sync="page"
      @current-change="load"
    />

    <!-- Detail -->
    <el-drawer :visible.sync="detailVisible" size="min(720px, 96vw)" :with-header="false">
      <div v-loading="detailLoading" class="detail">
        <template v-if="detail">
          <div class="detail-top">
            <el-tag size="small" :type="statusTag(detail.status)">{{ statusLabel(detail.status) }}</el-tag>
            <el-tag size="small" :type="detail.proposal_type === 'refine_collection' ? 'warning' : ''">{{ typeLabel(detail.proposal_type) }}</el-tag>
            <span v-if="detail.priority" class="priority" :class="'p' + detail.priority">P{{ detail.priority }}</span>
          </div>
          <h2>{{ detail.title }}</h2>
          <p class="main-idea">{{ detail.main_idea }}</p>

          <dl class="facts">
            <template v-if="detail.target_collection">
              <dt>Refines</dt><dd>{{ detail.target_collection.title }} <span class="muted">({{ detail.target_collection.id }})</span></dd>
            </template>
            <dt>Proposed curators</dt><dd>{{ curatorNames(detail) || '—' }}</dd>
            <dt>Series</dt><dd>{{ detail.series || '—' }}</dd>
            <dt>Direction</dt><dd>{{ directionLabel(detail.direction) || '—' }}</dd>
            <dt>Time span</dt><dd>{{ timeSpanLabel(detail.time_span) || '—' }}</dd>
            <dt>Tags</dt><dd><el-tag v-for="t in detail.tags || []" :key="t" size="mini" class="tag">{{ t }}</el-tag><span v-if="!(detail.tags || []).length">—</span></dd>
            <dt>Planning doc</dt><dd class="mono">{{ detail.doc_path || '—' }}</dd>
          </dl>

          <h3>Sources</h3>
          <ul v-if="(detail.sources || []).length" class="plain-list">
            <li v-for="(s, i) in detail.sources" :key="'s' + i">
              <el-tag size="mini" type="info">{{ sourceTypeLabel(s.type) }}</el-tag>
              <a v-if="safeUrl(s.url)" :href="safeUrl(s.url)" target="_blank" rel="noopener noreferrer">{{ s.title || s.url }}</a>
              <span v-else>{{ s.title }}</span>
              <div v-if="s.note" class="sub">{{ s.note }}</div>
            </li>
          </ul>
          <p v-else class="muted">No sources recorded.</p>

          <h3>Correlations</h3>
          <ul v-if="(detail.correlations || []).length" class="plain-list">
            <li v-for="(c, i) in detail.correlations" :key="'c' + i">
              <el-tag size="mini">{{ relationLabel(c.relation) }}</el-tag>
              <span>{{ c.label }}</span>
              <span class="muted"> · {{ c.target_type }}<template v-if="c.target_id"> · {{ c.target_id }}</template></span>
              <div v-if="c.note" class="sub">{{ c.note }}</div>
            </li>
          </ul>
          <p v-else class="muted">No correlations recorded.</p>

          <h3>Description</h3>
          <pre v-if="detail.description" class="description">{{ detail.description }}</pre>
          <p v-else class="muted">No description.</p>

          <h3>Status history</h3>
          <el-timeline>
            <el-timeline-item v-for="(h, i) in [...(detail.status_history || [])].reverse()" :key="'h' + i" :timestamp="formatDateTime(h.at)">
              <el-tag size="mini" :type="statusTag(h.status)">{{ statusLabel(h.status) }}</el-tag>
              <span v-if="h.note" class="history-note">{{ h.note }}</span>
            </el-timeline-item>
          </el-timeline>

          <div class="detail-actions">
            <el-button @click="openEditor(detail)">Edit</el-button>
            <el-button type="primary" plain @click="openStatus(detail)">Change status</el-button>
            <el-button type="danger" plain @click="remove(detail)">Delete</el-button>
          </div>
        </template>
      </div>
    </el-drawer>

    <!-- Editor -->
    <el-dialog
      :title="form.id ? 'Edit Proposal' : 'New Proposal'"
      :visible.sync="editorVisible"
      width="min(820px, 96vw)"
      top="4vh"
      :close-on-click-modal="false"
      :before-close="closeEditor"
    >
      <el-form label-position="top" :model="form" :disabled="saving">
        <el-form-item label="Proposal type" required>
          <el-radio-group v-model="form.proposal_type">
            <el-radio-button v-for="t in types" :key="t.value" :label="t.value">{{ t.label }}</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="form.proposal_type === 'refine_collection'" label="Collection to refine" required>
          <el-select
            v-model="form.target_collection_id"
            filterable
            remote
            clearable
            :remote-method="searchCollections"
            :loading="collectionSearchLoading"
            placeholder="Search existing collections"
            style="width: 100%"
          >
            <el-option v-for="c in collectionOptions" :key="c.id" :label="c.title" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="Title" required>
          <el-input v-model="form.title" maxlength="200" show-word-limit aria-label="Title" />
        </el-form-item>
        <el-form-item label="Main idea" required>
          <el-input v-model="form.main_idea" type="textarea" :rows="3" maxlength="2000" show-word-limit aria-label="Main idea" />
        </el-form-item>
        <el-form-item label="Description (Markdown: structure, scope, cases, open questions)">
          <el-input v-model="form.description" type="textarea" :rows="8" maxlength="20000" show-word-limit aria-label="Description" />
        </el-form-item>

        <div class="grid">
          <el-form-item label="Proposed curators / channels">
            <el-select
              v-model="form.curator_names"
              multiple
              filterable
              allow-create
              default-first-option
              remote
              :remote-method="searchCurators"
              :loading="curatorSearchLoading"
              placeholder="Search accounts or type a channel name"
              style="width: 100%"
            >
              <el-option v-for="c in curatorOptions" :key="c.id || c.name" :label="c.name" :value="c.name" />
            </el-select>
          </el-form-item>
          <el-form-item label="Series">
            <el-input v-model="form.series" maxlength="120" placeholder="e.g. Industry Foundations" />
          </el-form-item>
          <el-form-item label="Direction">
            <el-select v-model="form.direction" clearable style="width: 100%">
              <el-option v-for="d in directions" :key="d.value" :label="d.label" :value="d.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="Time span">
            <el-select v-model="form.time_span" clearable style="width: 100%">
              <el-option v-for="d in timeSpans" :key="d.value" :label="d.label" :value="d.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="Priority">
            <el-select v-model="form.priority" clearable placeholder="None" style="width: 100%">
              <el-option v-for="n in 5" :key="n" :label="'P' + n + (n === 1 ? ' (highest)' : '')" :value="n" />
            </el-select>
          </el-form-item>
          <el-form-item label="Tags">
            <el-select v-model="form.tags" multiple filterable allow-create default-first-option placeholder="Type and press Enter" style="width: 100%" />
          </el-form-item>
        </div>
        <el-form-item label="Planning doc path">
          <el-input v-model="form.doc_path" maxlength="500" placeholder="00 Plans/…" />
        </el-form-item>

        <el-form-item label="Sources">
          <div v-for="(s, i) in form.sources" :key="'fs' + i" class="row-line">
            <el-select v-model="s.type" size="small" style="width: 150px">
              <el-option v-for="t in sourceTypes" :key="t.value" :label="t.label" :value="t.value" />
            </el-select>
            <el-input v-model="s.title" size="small" placeholder="Title" maxlength="300" />
            <el-input v-model="s.url" size="small" placeholder="https://…" />
            <el-input v-model="s.note" size="small" placeholder="Note" maxlength="1000" />
            <el-button size="small" icon="el-icon-delete" aria-label="Remove source" @click="form.sources.splice(i, 1)" />
          </div>
          <el-button size="small" icon="el-icon-plus" :disabled="form.sources.length >= 10" @click="form.sources.push(blankSource())">Add source</el-button>
        </el-form-item>

        <el-form-item label="Correlations (related collections, proposals or outside work)">
          <div v-for="(c, i) in form.correlations" :key="'fc' + i" class="row-line">
            <el-select v-model="c.relation" size="small" style="width: 140px">
              <el-option v-for="r in relations" :key="r.value" :label="r.label" :value="r.value" />
            </el-select>
            <el-select v-model="c.target_type" size="small" style="width: 120px">
              <el-option v-for="t in targetTypes" :key="t" :label="t" :value="t" />
            </el-select>
            <el-input v-model="c.label" size="small" placeholder="Label (required)" maxlength="200" />
            <el-input v-model="c.target_id" size="small" placeholder="Record ID (optional)" class="id-input" />
            <el-input v-model="c.note" size="small" placeholder="Note" maxlength="1000" />
            <el-button size="small" icon="el-icon-delete" aria-label="Remove correlation" @click="form.correlations.splice(i, 1)" />
          </div>
          <el-button size="small" icon="el-icon-plus" :disabled="form.correlations.length >= 20" @click="form.correlations.push(blankCorrelation())">Add correlation</el-button>
        </el-form-item>

        <el-form-item v-if="!form.id" label="Initial status">
          <el-select v-model="form.status" style="width: 240px">
            <el-option v-for="s in statuses" :key="s.value" :label="s.label" :value="s.value" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :disabled="saving" @click="editorVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="saving" @click="save">Save Proposal</el-button>
      </span>
    </el-dialog>

    <!-- Import -->
    <el-dialog title="Import proposals" :visible.sync="importVisible" width="min(720px, 96vw)" :close-on-click-modal="false" :before-close="closeImport">
      <p class="hint">
        Paste a JSON array of proposals, or choose a .json file. Each item uses the same fields as the form
        (title, main_idea, proposal_type, target_collection_id, proposed_curators, sources, correlations, …).
        Items with an <code>external_key</code> update the existing proposal with that key, so an import can be re-run safely.
      </p>
      <el-upload action="" accept=".json,application/json" :show-file-list="false" :before-upload="readImportFile" :disabled="importing">
        <el-button size="small" :disabled="importing">Choose .json file</el-button>
      </el-upload>
      <el-input v-model="importText" type="textarea" :rows="12" class="import-text" placeholder="[ { &quot;title&quot;: &quot;…&quot;, &quot;main_idea&quot;: &quot;…&quot; } ]" :disabled="importing" aria-label="Proposals JSON" />
      <ul v-if="importResults.length" class="plain-list import-results">
        <li v-for="(r, i) in importResults" :key="'ir' + i">
          <i :class="r.ok ? 'el-icon-success ok' : 'el-icon-error bad'" />
          <span>{{ r.title }}</span>
          <span class="sub"> · {{ r.msg }}</span>
        </li>
      </ul>
      <span slot="footer">
        <el-button :disabled="importing" @click="importVisible = false">Close</el-button>
        <el-button type="primary" :loading="importing" @click="runImport">Import</el-button>
      </span>
    </el-dialog>

    <!-- Status -->
    <el-dialog title="Change status" :visible.sync="statusVisible" width="min(460px, 94vw)">
      <el-form label-position="top" :disabled="statusSaving">
        <el-form-item :label="statusTarget ? statusTarget.title : ''">
          <el-radio-group v-model="statusForm.status" class="status-options">
            <el-radio v-for="s in statuses" :key="s.value" :label="s.value">{{ s.label }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Note (optional)">
          <el-input v-model="statusForm.note" type="textarea" :rows="3" maxlength="1000" show-word-limit />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :disabled="statusSaving" @click="statusVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="statusSaving" @click="saveStatus">Update status</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  postRequest,
  listAdminCurators,
  listAdminCollections,
} from '@/apis/lfAdmin';

const STATUSES = [
  { value: 'proposed', label: 'Proposed', tag: 'info' },
  { value: 'accepted', label: 'Accepted', tag: 'success' },
  { value: 'parked', label: 'Parked', tag: 'warning' },
  { value: 'production_phase1', label: 'Production · Phase 1', tag: '' },
  { value: 'complete_phase1', label: 'Complete · Phase 1', tag: 'success' },
  { value: 'rejected', label: 'Rejected', tag: 'danger' },
];
const TYPES = [
  { value: 'new_collection', label: 'New collection' },
  { value: 'refine_collection', label: 'Refine existing' },
];
const DIRECTIONS = [
  { value: 'deeper', label: 'Deeper' },
  { value: 'bigger', label: 'Bigger' },
  { value: 'across', label: 'Across' },
  { value: 'practical', label: 'Practical' },
];
const TIME_SPANS = [
  { value: 'evergreen', label: 'Evergreen' },
  { value: 'multi_season', label: 'Multi-season' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'timely', label: 'Timely' },
];
const SOURCE_TYPES = [
  { value: 'signal_essay', label: 'Signal essay' },
  { value: 'social_post', label: 'Social post' },
  { value: 'video', label: 'Video' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'news', label: 'News' },
  { value: 'article', label: 'Article' },
  { value: 'reader_request', label: 'Reader request' },
  { value: 'internal', label: 'Internal' },
  { value: 'other', label: 'Other' },
];
const RELATIONS = [
  { value: 'related', label: 'Related' },
  { value: 'overlaps', label: 'Overlaps' },
  { value: 'extends', label: 'Extends' },
  { value: 'module_of', label: 'Module of' },
  { value: 'series_with', label: 'Series with' },
  { value: 'derived_from', label: 'Derived from' },
];
const find = (list, value) => list.find((x) => x.value === value);

export default {
  name: 'IdeaProposalsAdmin',
  data: () => ({
    statuses: STATUSES,
    types: TYPES,
    directions: DIRECTIONS,
    timeSpans: TIME_SPANS,
    sourceTypes: SOURCE_TYPES,
    relations: RELATIONS,
    targetTypes: ['collection', 'proposal', 'external'],
    filters: { status: '', proposal_type: '', q: '' },
    items: [],
    counts: {},
    total: 0,
    page: 1,
    pageSize: 25,
    loading: false,
    loadError: '',
    detailVisible: false,
    detailLoading: false,
    detail: null,
    editorVisible: false,
    saving: false,
    form: {},
    curatorOptions: [],
    curatorSearchLoading: false,
    collectionOptions: [],
    collectionSearchLoading: false,
    statusVisible: false,
    statusSaving: false,
    statusTarget: null,
    statusForm: { status: 'proposed', note: '' },
    searchTimer: null,
    importVisible: false,
    importing: false,
    importText: '',
    importResults: [],
  }),
  computed: {
    allCount() {
      return Object.values(this.counts).reduce((a, b) => a + (Number(b) || 0), 0);
    },
  },
  mounted() {
    this.form = this.blankForm();
    this.load();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    blankSource: () => ({ type: 'signal_essay', title: '', url: '', note: '' }),
    blankCorrelation: () => ({ relation: 'related', target_type: 'collection', target_id: '', label: '', note: '' }),
    blankForm() {
      return {
        id: '', proposal_type: 'new_collection', target_collection_id: '', title: '', main_idea: '', description: '',
        curator_names: [], curator_ids: {}, series: '', direction: '', time_span: '', priority: null, tags: [],
        doc_path: '', external_key: '', sources: [], correlations: [], status: 'proposed',
      };
    },
    statusLabel(v) { return (find(STATUSES, v) || {}).label || v; },
    statusTag(v) { return (find(STATUSES, v) || {}).tag || ''; },
    typeLabel(v) { return (find(TYPES, v) || {}).label || v; },
    directionLabel(v) { return (find(DIRECTIONS, v) || {}).label || ''; },
    timeSpanLabel(v) { return (find(TIME_SPANS, v) || {}).label || ''; },
    sourceTypeLabel(v) { return (find(SOURCE_TYPES, v) || {}).label || v; },
    relationLabel(v) { return (find(RELATIONS, v) || {}).label || v; },
    curatorNames(row) { return (row.proposed_curators || []).map((c) => c.name).join(', '); },
    formatDate(ts) { return ts ? new Date(ts * 1000).toLocaleDateString() : ''; },
    formatDateTime(ts) { return ts ? new Date(ts * 1000).toLocaleString() : ''; },
    safeUrl(value) {
      try {
        const url = new URL(value);
        return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
      } catch (e) {
        return undefined;
      }
    },
    async request(name, params = {}) {
      const res = await postRequest(name, params);
      if (res.data.code !== 0) throw new Error(res.data.msg || 'Request failed');
      return res.data.data;
    },
    debouncedReload() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, 350);
    },
    reload() {
      this.page = 1;
      this.load();
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const data = await this.request('adminListCollectionIdeaProposals', {
          ...this.filters, page: this.page, page_size: this.pageSize,
        });
        this.items = data.items || [];
        this.total = data.total || 0;
        this.counts = data.status_counts || {};
      } catch (e) {
        this.loadError = e.message || 'Could not load proposals';
      } finally {
        this.loading = false;
      }
    },
    async openDetail(row) {
      this.detailVisible = true;
      this.detailLoading = true;
      this.detail = row;
      try {
        this.detail = await this.request('adminGetCollectionIdeaProposal', { id: row.id });
      } catch (e) {
        this.$message.error(e.message || 'Could not load proposal');
      } finally {
        this.detailLoading = false;
      }
    },
    async openEditor(row) {
      let full = row;
      if (row && row.description === undefined) {
        try {
          full = await this.request('adminGetCollectionIdeaProposal', { id: row.id });
        } catch (e) {
          return this.$message.error(e.message || 'Could not load proposal');
        }
      }
      const f = this.blankForm();
      if (full) {
        Object.assign(f, {
          id: full.id, proposal_type: full.proposal_type, title: full.title, main_idea: full.main_idea,
          description: full.description || '', series: full.series || '', direction: full.direction || '',
          time_span: full.time_span || '', priority: full.priority || null, tags: [...(full.tags || [])],
          doc_path: full.doc_path || '', external_key: full.external_key || '',
          sources: (full.sources || []).map((s) => ({ ...s })),
          correlations: (full.correlations || []).map((c) => ({ ...c })),
          target_collection_id: full.target_collection ? full.target_collection.id : '',
          curator_names: (full.proposed_curators || []).map((c) => c.name),
          curator_ids: Object.fromEntries((full.proposed_curators || []).map((c) => [c.name, c.id || ''])),
        });
        this.collectionOptions = full.target_collection ? [full.target_collection] : [];
        this.curatorOptions = (full.proposed_curators || []).map((c) => ({ ...c }));
      }
      this.form = f;
      this.editorVisible = true;
    },
    closeEditor(done) {
      if (!this.saving) done();
    },
    async searchCurators(q) {
      this.curatorSearchLoading = true;
      try {
        const res = await listAdminCurators({ q, page_size: 20 });
        const rows = (res.data && res.data.data && res.data.data.rows) || [];
        rows.forEach((r) => {
          const name = r.name || r.user_nickName || r.user_email;
          if (name) this.form.curator_ids[name] = r.id;
        });
        this.curatorOptions = rows
          .map((r) => ({ id: r.id, name: r.name || r.user_nickName || r.user_email }))
          .filter((r) => r.name);
      } finally {
        this.curatorSearchLoading = false;
      }
    },
    async searchCollections(q) {
      this.collectionSearchLoading = true;
      try {
        const res = await listAdminCollections({ q, page_size: 20, record_kind: 'existing' });
        const rows = (res.data && res.data.data && res.data.data.rows) || [];
        this.collectionOptions = rows.map((r) => ({ id: r.id, title: r.title || r.id }));
      } finally {
        this.collectionSearchLoading = false;
      }
    },
    async save() {
      const f = this.form;
      if (!f.title.trim() || !f.main_idea.trim()) return this.$message.error('Enter a title and the main idea');
      if (f.proposal_type === 'refine_collection' && !f.target_collection_id) return this.$message.error('Choose the collection to refine');
      if (f.sources.some((s) => s.url && !this.safeUrl(s.url))) return this.$message.error('Source URLs must start with http:// or https://');
      if (f.correlations.some((c) => !c.label.trim())) return this.$message.error('Every correlation needs a label');
      const payload = {
        ...f,
        target_collection_id: f.proposal_type === 'refine_collection' ? f.target_collection_id : '',
        proposed_curators: f.curator_names.map((name) => ({ id: /^[a-f0-9]{24}$/i.test(f.curator_ids[name] || '') ? f.curator_ids[name] : '', name })),
        sources: f.sources.filter((s) => s.title.trim() || s.url.trim()),
      };
      delete payload.curator_names;
      delete payload.curator_ids;
      if (payload.id) delete payload.status;
      this.saving = true;
      try {
        const saved = await this.request('adminSaveCollectionIdeaProposal', payload);
        this.editorVisible = false;
        this.$message.success('Proposal saved');
        if (this.detail && this.detail.id === saved.id) this.detail = saved;
        await this.load();
      } catch (e) {
        this.$message.error(e.message || 'Could not save proposal');
      } finally {
        this.saving = false;
      }
    },
    openImport() {
      this.importText = '';
      this.importResults = [];
      this.importVisible = true;
    },
    closeImport(done) {
      if (!this.importing) done();
    },
    readImportFile(file) {
      if (file.size > 2 * 1024 * 1024) {
        this.$message.error('Choose a JSON file up to 2 MB');
        return false;
      }
      const reader = new FileReader();
      reader.onload = () => { this.importText = String(reader.result || ''); };
      reader.onerror = () => this.$message.error('Could not read file');
      reader.readAsText(file);
      return false;
    },
    async runImport() {
      let rows;
      try {
        rows = JSON.parse(this.importText);
      } catch (e) {
        return this.$message.error('Not valid JSON');
      }
      if (!Array.isArray(rows)) rows = [rows];
      if (!rows.length || rows.length > 200) return this.$message.error('Import 1–200 proposals at a time');
      this.importing = true;
      this.importResults = [];
      try {
        for (const row of rows) {
          const title = (row && row.title) || '(untitled)';
          try {
            const saved = await this.request('adminSaveCollectionIdeaProposal', row);
            this.importResults.push({ ok: true, title, msg: saved.create_time === saved.update_time ? 'created' : 'updated' });
          } catch (e) {
            this.importResults.push({ ok: false, title, msg: e.message || 'failed' });
          }
        }
        const failed = this.importResults.filter((r) => !r.ok).length;
        if (failed) this.$message.warning(`${rows.length - failed} imported, ${failed} failed`);
        else this.$message.success(`${rows.length} imported`);
        await this.load();
      } finally {
        this.importing = false;
      }
    },
    openStatus(row) {
      this.statusTarget = row;
      this.statusForm = { status: row.status, note: '' };
      this.statusVisible = true;
    },
    async saveStatus() {
      this.statusSaving = true;
      try {
        const saved = await this.request('adminUpdateCollectionIdeaProposalStatus', { id: this.statusTarget.id, ...this.statusForm });
        this.statusVisible = false;
        this.$message.success(`Status: ${this.statusLabel(saved.status)}`);
        if (this.detail && this.detail.id === saved.id) this.detail = saved;
        await this.load();
      } catch (e) {
        this.$message.error(e.message || 'Could not update status');
      } finally {
        this.statusSaving = false;
      }
    },
    async remove(row) {
      try {
        await this.$confirm(`Delete “${row.title}”? This cannot be undone.`, 'Delete Proposal', { type: 'warning' });
      } catch (e) {
        return;
      }
      try {
        await this.request('adminDeleteCollectionIdeaProposal', { id: row.id });
        this.detailVisible = false;
        this.$message.success('Proposal deleted');
        await this.load();
      } catch (e) {
        this.$message.error(e.message || 'Could not delete proposal');
      }
    },
  },
};
</script>

<style scoped>
.idea-proposals { padding: 24px; }
.header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
h2 { margin: 0 0 8px; }
p { color: #606266; }
.filters { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; }
.filter-row { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.alert { margin-bottom: 12px; }
.row-actions { display: flex; flex-wrap: nowrap; }
.row-actions .el-button + .el-button { margin-left: 6px; }
.idea-proposals >>> .el-table .cell { word-break: normal; overflow-wrap: break-word; }
.title-link { font-weight: 600; color: #1f2d3d; text-decoration: none; }
.title-link:hover { color: #0061f8; }
.idea { color: #606266; font-size: 12px; line-height: 1.5; margin-top: 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.sub { color: #909399; font-size: 12px; margin-top: 2px; }
.muted { color: #909399; }
.priority { display: inline-block; font-weight: 700; font-size: 12px; padding: 1px 6px; border-radius: 4px; background: #f0f2f5; color: #606266; }
.priority.p1 { background: #fde2e2; color: #c03639; }
.priority.p2 { background: #fdf2e2; color: #b2770b; }
.pager { margin-top: 16px; text-align: right; }
.detail { padding: 24px 28px 40px; }
.detail h2 { margin: 12px 0 8px; }
.detail h3 { margin: 24px 0 8px; font-size: 15px; }
.detail-top { display: flex; gap: 8px; align-items: center; }
.main-idea { font-size: 15px; line-height: 1.6; color: #303133; }
.facts { display: grid; grid-template-columns: 150px 1fr; gap: 6px 12px; margin: 16px 0 0; font-size: 13px; }
.facts dt { color: #909399; }
.facts dd { margin: 0; overflow-wrap: anywhere; }
.mono { font-family: monospace; font-size: 12px; }
.tag { margin-right: 4px; }
.plain-list { list-style: none; padding: 0; margin: 0; }
.plain-list li { padding: 6px 0; border-bottom: 1px solid #f0f2f5; }
.plain-list .el-tag { margin-right: 6px; }
.plain-list a { overflow-wrap: anywhere; }
.description { white-space: pre-wrap; font-family: inherit; font-size: 13px; line-height: 1.6; background: #f8f9fb; border: 1px solid #ebeef5; border-radius: 6px; padding: 12px 14px; max-height: 480px; overflow: auto; margin: 0; }
.history-note { margin-left: 8px; color: #606266; }
.detail-actions { display: flex; gap: 8px; margin-top: 24px; }
.grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 16px; }
.row-line { display: flex; gap: 6px; margin-bottom: 6px; align-items: center; }
.row-line > .el-select { flex: 0 0 auto; }
.row-line > .el-input { flex: 1 1 120px; min-width: 0; }
.row-line > .el-input.id-input { flex: 0 1 210px; }
.row-line > .el-button { flex: 0 0 auto; }
.header-actions { display: flex; gap: 8px; }
.hint { font-size: 13px; line-height: 1.6; word-break: normal; overflow-wrap: break-word; }
.import-text { margin-top: 12px; font-family: monospace; }
.import-results { margin-top: 12px; max-height: 220px; overflow: auto; }
.import-results .ok { color: #67c23a; margin-right: 6px; }
.import-results .bad { color: #f56c6c; margin-right: 6px; }
.status-options { display: flex; flex-direction: column; gap: 10px; }
@media (max-width: 720px) {
  .grid { grid-template-columns: 1fr; }
  .row-line { flex-wrap: wrap; }
}
</style>
